/**
 * WHETHER AN EMAIL WOULD GO PAST OPS'S OWN SWITCH FOR CLIENT EMAIL, AND THE LINE THAT SAYS WHERE IT
 * STANDS (ops #76 O6), texting's twin (`TextingGate.tsx`).
 *
 * Home delivers every client email ops composes, from home@, and ops holds a go-live switch of its own
 * for them, off until an owner moves it on the Settings screen: off, test addresses only, or on.
 * `GET /api/me` tells the office where it stands (`me.emailGoLive`), and the two places a person
 * composes an email of her own words, the move flow and "Send the invoice", ask here whether an email
 * to one address would go: yes while it is on, or at test addresses for an address on the list, as
 * home reads an address. Where it would not, the email is offered disabled with the switch named, and
 * the typed reason or copying her link stays the way on. The server asks the switch again at every
 * email it composes and parks one it refuses, so this is UX and never the control (binding rule 6).
 *
 * Home answers for an email on the email itself, so unlike texting's there is no other system's word
 * to draw beneath the line. Once the switch is on there is no line at all.
 */
import type { EmailGoLive } from '../api.ts';
import { EMAIL_GO_LIVE_LINES } from '../labels.ts';

export interface EmailPath {
  /** Whether an email to this address would go past ops's switch. */
  readonly open: boolean;
  /** Where the switch stands, as the one line; null once it is on. */
  readonly line: string | null;
  /** Where an email would not go, the sentence naming the switch; null where it would. */
  readonly why: string | null;
}

const OFF_WHY = 'No email goes from ops until an owner moves the email switch in Settings.';
const NOT_LISTED_WHY =
  'Ops emails only the test addresses listed beside the email switch in Settings, and their address is not one of them.';

/** An address as home reads it: trimmed and in lower case, so the list and her record need not agree. */
const spelled = (address: string): string => address.trim().toLowerCase();

/**
 * The switch as it applies to one address, or to none yet. Absent reads as off, the safe direction:
 * the office is always told, and a screen that was not must not offer an email the server would park.
 */
export function emailPath(view: EmailGoLive | null | undefined, address: string | null): EmailPath {
  const state = view?.state ?? 'off';
  const listed =
    address !== null && (view?.addresses ?? []).map(spelled).includes(spelled(address));
  const open = state === 'on' || (state === 'test_addresses' && listed);
  return {
    open,
    line: EMAIL_GO_LIVE_LINES[state],
    why: open ? null : state === 'test_addresses' ? NOT_LISTED_WHY : OFF_WHY,
  };
}

/**
 * The line, and where an email would not go, why not and the way on, in the screen's own words.
 * Nothing at all once client email is on.
 */
export function EmailGate({ path, wayOn }: { path: EmailPath; wayOn: string }) {
  if (path.line === null) return null;
  return (
    <div
      id="email-gate"
      className={`mb-3 rounded-lg border p-3 ${
        path.open ? 'border-navy/20' : 'border-coral/50 border-l-4 border-l-coral bg-parchment'
      }`}
    >
      <p className="text-sm font-medium">{path.line}.</p>
      {path.why ? (
        <p className="mt-1 text-sm">
          {path.why} {wayOn}
        </p>
      ) : null}
    </div>
  );
}
